/**
 * Add Pre-Printed Stock Modal
 *
 * Supports two scenarios for recording externally pre-printed stock:
 * 1. "Their blanks" — external printer supplied blanks, only increments product stock
 * 2. "Our blanks" — we sent blanks to external printer, decrements blank + increments product
 *
 * Uses the prePrint.addPrePrintedStock tRPC mutation.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, Package, Search } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useLocation } from '@/contexts/LocationContext';
import { useProductSearch, useBlankSkuMatch } from '@/features/production-orders/hooks/usePrePrint';
import { parseSupplyRefusal } from '@/lib/trpc-error-utils';
import { preprintRefusedMessage } from '../utils/supplyAliasCopy';

type Source = 'their_blanks' | 'our_blanks';

interface SelectedProduct {
  skuId: string;
  sku: string;
  name: string;
  imageUrl: string | null;
  size: string | null;
  color: string | null;
  /** Shopify productType — used as fallback category for blank matching when product doesn't exist locally */
  productType: string | null;
  /** Shopify product GID for auto-grouping (e.g., "gid://shopify/Product/12345") */
  shopifyProductId: string | null;
}

interface AddPrePrintedStockModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Sub-component: Blank SKU match display.
 * Must be a separate component because useBlankSkuMatch is a hook
 * that can only be called at the top level (not inside conditionals).
 */
function BlankSkuSection({
  locationId,
  productSku,
  productType,
  size,
  color,
  quantity,
  onBlankSkuChange,
}: {
  locationId: string;
  productSku: string;
  productType?: string | null;
  size?: string | null;
  color?: string | null;
  quantity: number;
  onBlankSkuChange: (blankSkuId: string | null, blankSku: string | null, productSkuLocalId: string | null) => void;
}) {
  const { match, isLoading, error } = useBlankSkuMatch(locationId, productSku, {
    productCategory: productType || undefined,
    productSize: size || undefined,
    productColor: color || undefined,
  });

  // Notify parent of blank SKU match changes
  useEffect(() => {
    if (match) {
      onBlankSkuChange(match.skuId, match.sku, match.productSkuId ?? null);
    } else {
      onBlankSkuChange(null, null, null);
    }
  }, [match?.skuId]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-run when skuId changes

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/30 rounded-md">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Finding matching blank garment...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 p-3 bg-red-50 rounded-md border border-red-200">
        <AlertTriangle className="h-3.5 w-3.5" />
        Failed to find matching blank garment
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flex items-center gap-2 text-sm text-amber-700 p-3 bg-amber-50 rounded-md border border-amber-200">
        <AlertTriangle className="h-3.5 w-3.5" />
        No matching blank garment found for this product
      </div>
    );
  }

  const stockInsufficient = match.currentStock < quantity;

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Matched Blank Garment</Label>
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-md border">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-sm font-medium">{match.sku}</span>
        </div>
        <Badge variant={stockInsufficient ? 'warning' : 'success'} size="sm">
          {match.currentStock} in stock
        </Badge>
      </div>
      {stockInsufficient && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />
          Blank stock ({match.currentStock}) is less than requested quantity ({quantity}).
          Stock will go negative.
        </p>
      )}
    </div>
  );
}

export default function AddPrePrintedStockModal({ isOpen, onClose }: AddPrePrintedStockModalProps) {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id ?? '';

  // Form state
  const [source, setSource] = useState<Source>('their_blanks');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<SelectedProduct | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [blankSkuId, setBlankSkuId] = useState<string | null>(null);
  const [blankSku, setBlankSku] = useState<string | null>(null);
  /** Local DB UUID for the product SKU (resolved via blank match lookup) */
  const [productSkuLocalId, setProductSkuLocalId] = useState<string | null>(null);
  /** Show negative stock confirmation dialog */
  const [showNegativeStockConfirm, setShowNegativeStockConfirm] = useState(false);

  // Product search
  const { products: searchResults, isLoading: searchLoading } = useProductSearch(searchQuery);

  // Always resolve the product SKU's local ID via blank match lookup
  // This is needed because Shopify search returns GIDs, not local UUIDs
  // Pass fallback attrs so blank matching works even when product doesn't exist locally
  //
  // The resolved blank match is also the client-side mirror of the server's
  // pre-print eligibility guard (Plan 02): a SKU with no derivable blank OR a
  // derived blank that isn't in the org's POD catalog both surface here as
  // `blankMatch === null`. We gate submit on it for BOTH sources so the
  // "no derivable blank" rejection is prevented before submit, not surfaced as
  // a raw error toast after a failed mutation.
  const {
    match: blankMatch,
    isLoading: blankMatchLoading,
    error: blankMatchError,
  } = useBlankSkuMatch(
    locationId,
    selectedProduct?.sku ?? '',
    {
      productCategory: selectedProduct?.productType || undefined,
      productSize: selectedProduct?.size || undefined,
      productColor: selectedProduct?.color || undefined,
    }
  );

  // Update productSkuLocalId when blank match resolves
  useEffect(() => {
    if (blankMatch?.productSkuId) {
      setProductSkuLocalId(blankMatch.productSkuId);
    }
  }, [blankMatch?.productSkuId]);

  // Flatten product variants for display
  const flatVariants = useMemo(() => {
    const variants: Array<{
      skuId: string;
      sku: string;
      name: string;
      imageUrl: string | null;
      size: string | null;
      color: string | null;
      productType: string | null;
      shopifyProductId: string;
    }> = [];

    for (const product of searchResults) {
      for (const variant of product.variants) {
        variants.push({
          skuId: variant.shopifyVariantId,
          sku: variant.sku,
          name: product.title || variant.sku,
          imageUrl: product.imageUrl,
          size: variant.size,
          color: variant.color,
          productType: product.productType,
          shopifyProductId: product.shopifyProductId,
        });
      }
    }

    return variants;
  }, [searchResults]);

  const utils = trpc.useUtils();

  const mutation = trpc.prePrint.addPrePrintedStock.useMutation({
    onSuccess: (data) => {
      toast.success('Pre-printed stock added', {
        description: `${quantity} x ${selectedProduct?.sku} added to stock${data.productStock != null ? ` (new total: ${data.productStock})` : ''}`,
      });
      void utils.inventory.getProducts.invalidate();
      void utils.inventory.listProductsGrouped.invalidate();
      handleClose();
    },
    onError: (error) => {
      // Phase 92 (D-13, UI-SPEC Screen 4 R4): a SKU built on the 3PL's blank
      // prints to order. Fixed copy, never the raw marker. The 3PL's name rides
      // the cause (dropped on the wire for now); a client only meets this at
      // the shared location, whose owner is that 3PL.
      const refusal = parseSupplyRefusal(error);
      if (refusal?.kind === 'preprint_refused') {
        toast.error('Failed to add pre-printed stock', {
          description: preprintRefusedMessage(t, {
            sku: refusal.sku ?? selectedProduct?.sku ?? '',
            ownerName:
              refusal.ownerName ??
              (selectedLocation?.is_shared ? selectedLocation.owner_organization_name : null),
          }),
        });
        return;
      }
      toast.error('Failed to add pre-printed stock', {
        description: error.message,
      });
    },
  });

  const resetForm = () => {
    setSource('their_blanks');
    setSearchQuery('');
    setSelectedProduct(null);
    setQuantity(1);
    setNotes('');
    setBlankSkuId(null);
    setBlankSku(null);
    setProductSkuLocalId(null);
    setShowNegativeStockConfirm(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleBlankSkuChange = (id: string | null, sku: string | null, resolvedProductSkuId: string | null) => {
    setBlankSkuId(id);
    setBlankSku(sku);
    if (resolvedProductSkuId) {
      setProductSkuLocalId(resolvedProductSkuId);
    }
  };

  /** Whether blank stock will go negative with this submission */
  const willGoNegative = source === 'our_blanks' && blankMatch != null && blankMatch.currentStock < quantity;

  const doSubmit = () => {
    if (!selectedProduct || !locationId || quantity <= 0) return;

    if (source === 'our_blanks' && !blankSkuId) {
      toast.error('No matching blank garment found');
      return;
    }

    mutation.mutate({
      locationId,
      productSkuId: productSkuLocalId ?? '', // Empty string triggers backend auto-creation
      productSku: selectedProduct.sku,
      productName: selectedProduct.name || undefined,
      productSize: selectedProduct.size || undefined,
      productColor: selectedProduct.color || undefined,
      productImageUrl: selectedProduct.imageUrl || undefined,
      shopifyProductId: selectedProduct.shopifyProductId || undefined,
      productType: selectedProduct.productType || undefined,
      quantity,
      source,
      blankSkuId: source === 'our_blanks' ? blankSkuId! : undefined,
      blankSku: source === 'our_blanks' ? blankSku ?? undefined : undefined,
      notes: notes.trim() || undefined,
    });
  };

  const handleSubmit = () => {
    if (willGoNegative) {
      setShowNegativeStockConfirm(true);
      return;
    }
    doSubmit();
  };

  /**
   * Whether a blank garment resolved for the selected product. This is the
   * client-side mirror of the server's pre-print eligibility guard (Plan 02) and
   * is required for BOTH sources — a pre-print whose blank can't be derived (or
   * whose derived blank isn't in the catalog) has no match here and must not be
   * submittable. Previously `their_blanks` slipped through with no match.
   */
  const hasBlankMatch = blankMatch != null;
  /**
   * A transient fetch failure (network / 500) is NOT a true no-match — it leaves
   * us unable to tell. Surface a distinct "couldn't check, try again" notice for
   * it (mirroring the `our_blanks` / `BlankSkuSection` error branch) and reserve
   * the definitive "no derivable blank" copy for a settled, error-free no-match.
   */
  const showBlankMatchError =
    selectedProduct != null && !blankMatchLoading && blankMatchError != null;
  /** Show the "no derivable blank" notice once the match lookup has settled cleanly. */
  const showNoBlankMatch =
    selectedProduct != null && !blankMatchLoading && blankMatchError == null && !hasBlankMatch;

  const canSubmit =
    selectedProduct &&
    quantity > 0 &&
    locationId &&
    !mutation.isPending &&
    !blankMatchLoading &&
    hasBlankMatch;

  /**
   * Programmatic reason the submit button is disabled, so the disabled state is
   * never silent. Mirrors the visible notices (the `their_blanks` fetch-error /
   * no-derivable-blank gates) word-for-word where they apply, and falls back to
   * a generic prerequisite hint otherwise. Surfaced via a Tooltip-over-span and
   * `aria-describedby` on the trigger (mirrors PrePrintPage's pattern).
   */
  const submitDisabledReason: string | null = canSubmit
    ? null
    : !selectedProduct
      ? 'Choose a product to pre-print first.'
      : !locationId
        ? 'Choose a location first.'
        : quantity <= 0
          ? 'Enter a quantity of at least 1.'
          : blankMatchLoading
            ? 'Checking for a matching blank garment…'
            : showBlankMatchError
              ? "Couldn't check for a matching blank garment. Try again."
              : showNoBlankMatch
                ? `No base garment could be derived for ${selectedProduct.sku}.`
                : null;

  // Build confirmation text
  const confirmText = selectedProduct
    ? source === 'our_blanks' && blankSku
      ? `Adding ${quantity} x ${selectedProduct.sku} to pre-printed stock. Will also decrement ${quantity} x ${blankSku} (blank garments).`
      : `Adding ${quantity} x ${selectedProduct.sku} to pre-printed stock.`
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Pre-Printed Stock</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Source selection */}
          <div className="grid gap-2">
            <Label>Source</Label>
            <RadioGroup
              value={source}
              onValueChange={(v) => setSource(v as Source)}
              className="grid grid-cols-1 gap-2"
            >
              <label
                htmlFor="source-their"
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  source === 'their_blanks' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
              >
                <RadioGroupItem value="their_blanks" id="source-their" />
                <div>
                  <p className="text-sm font-medium">External printer (their blanks)</p>
                  <p className="text-xs text-muted-foreground">Only adds product stock</p>
                </div>
              </label>
              <label
                htmlFor="source-our"
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  source === 'our_blanks' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
              >
                <RadioGroupItem value="our_blanks" id="source-our" />
                <div>
                  <p className="text-sm font-medium">External printer (our blanks)</p>
                  <p className="text-xs text-muted-foreground">Decrements blank stock + adds product stock</p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {/* Product search */}
          <div className="grid gap-2">
            <Label>Product SKU</Label>
            {selectedProduct ? (
              <div className="flex items-center justify-between p-2.5 bg-muted/30 rounded-md border">
                <div className="flex items-center gap-3">
                  {selectedProduct.imageUrl ? (
                    <img
                      src={selectedProduct.imageUrl}
                      alt=""
                      className="h-10 w-10 rounded object-cover bg-muted flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="default">{selectedProduct.sku}</Badge>
                      {selectedProduct.size && (
                        <span className="text-xs text-muted-foreground">{selectedProduct.size}</span>
                      )}
                      {selectedProduct.color && (
                        <span className="text-xs text-muted-foreground">{selectedProduct.color}</span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {selectedProduct.name}
                    </span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs flex-shrink-0"
                  onClick={() => {
                    setSelectedProduct(null);
                    setSearchQuery('');
                    setBlankSkuId(null);
                    setBlankSku(null);
                    setProductSkuLocalId(null);
                  }}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search products by SKU or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                    autoFocus
                  />
                </div>
                {searchQuery.length >= 2 && (
                  <div className="max-h-64 overflow-y-auto rounded-md border bg-popover">
                    {searchLoading ? (
                      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Searching...
                      </div>
                    ) : flatVariants.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">
                        No products found
                      </div>
                    ) : (
                      flatVariants.map((variant) => (
                        <button
                          key={variant.skuId}
                          className="w-full flex items-center gap-3 p-2 text-left hover:bg-accent transition-colors border-b last:border-b-0"
                          onClick={() => {
                            setSelectedProduct(variant);
                            setSearchQuery('');
                          }}
                        >
                          {variant.imageUrl ? (
                            <img
                              src={variant.imageUrl}
                              alt=""
                              className="h-8 w-8 rounded object-cover bg-muted flex-shrink-0"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                          <span className="font-mono text-xs font-medium flex-shrink-0 min-w-[120px]">{variant.sku}</span>
                          <span className="text-sm text-muted-foreground flex-1 min-w-0">{variant.name}</span>
                          {variant.color && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">{variant.color}</span>
                          )}
                          {variant.size && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 flex-shrink-0">{variant.size}</Badge>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="grid gap-2">
            <Label htmlFor="preprint-quantity">Quantity</Label>
            <Input
              id="preprint-quantity"
              type="number"
              min={1}
              max={10000}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>

          {/* Blank garment section — only visible when "our blanks". It renders
              its own amber "no matching blank" notice for that source. */}
          {source === 'our_blanks' && selectedProduct && locationId && (
            <BlankSkuSection
              locationId={locationId}
              productSku={selectedProduct.sku}
              productType={selectedProduct.productType}
              size={selectedProduct.size}
              color={selectedProduct.color}
              quantity={quantity}
              onBlankSkuChange={handleBlankSkuChange}
            />
          )}

          {/* "Their blanks" blank-match notices, wrapped in a polite live region
              so they announce on appearance for screen-reader users (the notices
              gate submit, so a silent appearance would leave SR users unaware why
              the button is disabled). The two branches are mutually exclusive. */}
          <div aria-live="polite">
            {/* Blank-match fetch error ("their blanks"). A transient failure can't
                tell us whether a blank exists, so we MUST NOT show the definitive
                "no derivable blank" copy below — that would mislabel an outage as a
                finished PAP good. Mirrors the BlankSkuSection error branch. */}
            {source === 'their_blanks' && showBlankMatchError && (
              <div className="flex items-start gap-2 text-sm text-red-600 p-3 bg-red-50 rounded-md border border-red-200">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Couldn't check for a matching blank garment. Try again.
                </span>
              </div>
            )}

            {/* No-derivable-blank gate ("their blanks"). The "our blanks" path
                shows the same notice inside BlankSkuSection, so this only covers
                the source that previously slipped through with no match. Mirrors
                the server's pre-print eligibility rejection (Plan 02) as a
                gated-submit state, not a post-failure toast. Only shown once the
                lookup settles WITHOUT an error (a fetch error renders the notice
                above instead). */}
            {source === 'their_blanks' && showNoBlankMatch && (
              <div className="flex items-start gap-2 text-sm text-amber-700 p-3 bg-amber-50 rounded-md border border-amber-200">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Cannot pre-print {selectedProduct?.sku}: no base garment could be derived. This looks
                  like a finished PAP good, not a pre-printable design.
                </span>
              </div>
            )}
          </div>

          {/* Notes (optional) */}
          <div className="grid gap-2">
            <Label htmlFor="preprint-notes">
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="preprint-notes"
              placeholder="e.g., Screen printing batch from XYZ printer"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>

          {/* Confirmation text */}
          {confirmText && (
            <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md">
              {confirmText}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          {/* Disabled-submit reason is announced via a Tooltip over a <span>
              (Radix can't fire events on a disabled button) plus aria-describedby
              on the button, mirroring PrePrintPage's pattern. The reason matches
              the visible notice so both surfaces say the same thing. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={submitDisabledReason ? 0 : -1}>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  aria-describedby={submitDisabledReason ? 'preprint-submit-reason' : undefined}
                  className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Pre-Printed Stock'
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {submitDisabledReason && (
              <TooltipContent id="preprint-submit-reason">
                <p>{submitDisabledReason}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </DialogFooter>
      </DialogContent>

      {/* Negative stock confirmation dialog */}
      <AlertDialog open={showNegativeStockConfirm} onOpenChange={setShowNegativeStockConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Blank Stock Will Go Negative
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are requesting <strong>{quantity}</strong> units of{' '}
                  <strong>{blankSku}</strong>, but only{' '}
                  <strong>{blankMatch?.currentStock ?? 0}</strong>{' '}
                  {(blankMatch?.currentStock ?? 0) === 1 ? 'unit is' : 'units are'} in stock.
                </p>
                <p>
                  Blank stock will go to{' '}
                  <strong className="text-red-600">
                    {(blankMatch?.currentStock ?? 0) - quantity}
                  </strong>{' '}
                  after this operation. This indicates an overcommitment that should be resolved
                  by receiving more blank garments.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => {
                setShowNegativeStockConfirm(false);
                doSubmit();
              }}
            >
              Confirm: Allow Negative Stock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
